import React, { useState, useMemo } from 'react'
import uniq from 'lodash/uniq'
import styles from './ChartSelector.module.scss'
import { onChartTypeSelected } from '../../gaEvents'

// Replace the two green hex values baked into rawgraphs-charts icon data URIs
// with brand teal equivalents, without modifying node_modules.
export function tealifyIcon(uri) {
  if (!uri) return uri
  return uri
    .replace(/%2306c26c/gi, '%231E9696')  // dark green → dark teal
    .replace(/%2395e5c0/gi, '%23D0F0F0')  // light green → teal pale
    .replace(/%234bc06a/gi, '%232CBDBD')  // medium green → teal
}

function ChartSelector({ availableCharts, currentChart, setCurrentChart }) {
  const [filter, setFilter] = useState('all')

  const categories = useMemo(
    () => ['all', ...uniq(availableCharts.map((d) => d.metadata.categories).flat())],
    [availableCharts]
  )

  const charts = useMemo(
    () =>
      filter === 'all'
        ? availableCharts
        : availableCharts.filter((d) =>
            d.metadata.categories.map((c) => c.toLowerCase()).includes(filter.toLowerCase())
          ),
    [availableCharts, filter]
  )

  const outsideFilter =
    filter !== 'all' &&
    currentChart &&
    !charts.includes(currentChart)

  return (
    <div className={styles.shell}>
      {/* Category chips */}
      <div className={styles.chips}>
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            className={[styles.chip, filter === cat ? styles.chipActive : ''].join(' ')}
            onClick={() => setFilter(cat)}
          >
            {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </div>

      {/* Count row */}
      <div className={styles.head}>
        <span className={styles.count}>
          <b>{charts.length}</b> {charts.length === 1 ? 'chart type' : 'chart types'}
        </span>
        {outsideFilter && (
          <span className={styles.outsideNote}>
            <b>{currentChart.metadata.name}</b> is in another category
          </span>
        )}
      </div>

      {/* Card grid */}
      <div className={styles.grid}>
        {charts.map((d, i) => {
          const isSelected = d === currentChart
          return (
            <button
              key={d.metadata.id || i}
              type="button"
              className={[styles.card, isSelected ? styles.cardSelected : ''].join(' ')}
              onClick={() => {
                onChartTypeSelected(d.metadata.id, d.metadata.name)
                setCurrentChart(d)
              }}
            >
              {isSelected && (
                <span className={styles.checkmark} aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              )}
              <div
                className={styles.thumb}
                style={{ backgroundImage: `url("${tealifyIcon(d.metadata.icon)}")` }}
              />
              <div className={styles.cardBody}>
                <span className={styles.chartName}>{d.metadata.name}</span>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default React.memo(ChartSelector)
